import type { QueryClient, QueryKey } from "@tanstack/react-query"

/**
 * Snapshots the current cache value for `queryKey`, then applies `updater`
 * immediately. Call from `onMutate`; pass the returned snapshot to
 * `rollbackOptimistic` in `onError`. See design.md (Decisión 2) —
 * react-query's cache is the source of truth, no parallel local state.
 */
export async function applyOptimistic<T>(
  qc: QueryClient,
  queryKey: QueryKey,
  updater: (previous: T | undefined) => T | undefined,
): Promise<T | undefined> {
  await qc.cancelQueries({ queryKey })
  const previous = qc.getQueryData<T>(queryKey)
  qc.setQueryData<T>(queryKey, updater)
  return previous
}

/**
 * Restores the snapshot captured by `applyOptimistic`. Call from `onError`.
 * `setQueryData` treats a literal `undefined` value as "no update" (per
 * react-query semantics), so an absent snapshot is restored via
 * `removeQueries` instead — otherwise rolling back an optimistic insert
 * into an empty cache would silently keep the inserted value.
 */
export function rollbackOptimistic<T>(qc: QueryClient, queryKey: QueryKey, previous: T | undefined): void {
  if (previous === undefined) {
    qc.removeQueries({ queryKey, exact: true })
    return
  }
  qc.setQueryData<T>(queryKey, previous)
}
